import React, { useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { useAppConfig } from "@ct-react/core";
import { useLocaleNavigate } from "@ct-react/locale";
import { SiteType } from "@shared/site-type";
import {
  ANNUAL_ARTICLE_SUMMARY_FRAGMENT,
  RENTAL_ARTICLE_SUMMARY_FRAGMENT,
  SALE_ARTICLE_SUMMARY_FRAGMENT,
  SEASONAL_ARTICLE_SUMMARY_FRAGMENT
} from "@shared/gql/fragments";
import { rebuildQuerySearchParamsFromMain } from "../tools/search-params/search";
import { useDisplayMobileOnly } from "../tools/breakpoints";
import { MainSearchCriteria } from "../models/search";
import { AnyArticleSummary } from "../models/article";
import { useSeason } from "../hooks/generic";
import Wrapper from "../components/layout/wrapper";
import Summary from "../components/cards/summary";
import MainSearchForm from "../components/search/main-search-form";
import HomealpSearchForm from "../components/search/homealp-search-form";
import Comment from "../components/cards/comment";
import SeoHelmet from "../components/seo-helmet";
import summerBg from "../assets/backgrounds/home-head-bg-summer.webp";
import winterBg from "../assets/backgrounds/home-head-bg-winter.webp";
import seasonalpWinterBg from "../assets/backgrounds/seasonalp-home-head-bg-winter.webp";
import seasonalpSummerBg from "../assets/backgrounds/seasonalp-home-head-bg-summer.webp";
import homealpWinterBg from "../assets/backgrounds/homealp-home-head-bg-winter.webp";
import homealpSummerBg from "../assets/backgrounds/homealp-home-head-bg-summer.webp";
import "./common.scss";
import "./home.scss";

const transDefs = defineMessages({
  catch: {
    id: "home-catch-phrase",
    defaultMessage: "Le secret des vacances parfaites dans les Alpes !"
  },
  seasonalCatch: {
    id: "home-seasonal-catch-phrase",
    defaultMessage: "Les meilleures offres pour un long séjour dans les Alpes !"
  },
  saleCatch: {
    id: "home-sale-catch-phrase",
    defaultMessage: "Chalets et appartements dans un cadre unique au coeur des Alpes !"
  },
  searchTitle: {
    id: "home-search-title",
    defaultMessage: "Trouvez votre logement alpin idéal."
  },
  searchSaleTitle: {
    id: "home-search-sale-title",
    defaultMessage: "Pour quelques temps... ou pour la vie."
  },
  title: {
    id: "seo-title-home",
    defaultMessage: "Location de chalets et d'appartements de vacances à la montagne."
  },
  promo: {
    id: "home-promo-title",
    defaultMessage: "Choisissez une offre promotionnelle"
  },
  summaries: {
    id: "home-list-title",
    defaultMessage: "Une sélection qui pourrait vous plaire"
  },
  summariesSale: {
    id: "home-list-sale-title",
    defaultMessage: "Ne manquez pas l'opportunité de devenir propriétaire"
  },
  reviews: {
    id: "home-reviews-title",
    defaultMessage: "Ce que disent nos clients"
  }
});

const PROMOS_GQL_DATA = gql`
  ${RENTAL_ARTICLE_SUMMARY_FRAGMENT}
  query GetHomePromotions($agencyIds: [ID!]) {
    promos: search(first: 10, where: { any: { promoted: true, merchantIds: $agencyIds } }) {
      edges { node { ... on RentalAccommodationArticle { ...RentalArticleSummaryFields } } }
    }
  }
`;

const SUMMARIES_GQL_DATA = (type: SiteType) => {
  switch (type) {
    case SiteType.SEASONALP:
      return gql`
        ${SEASONAL_ARTICLE_SUMMARY_FRAGMENT}
        query GetHomeSummaries($agencyIds: [ID!]) {
          summaries: articleRentalpRandomize(accommodationType: SEASONAL, merchantIds: $agencyIds) {
            ... on SeasonalAccommodationArticle { ...SeasonalArticleSummaryFields }
          }
        }
      `;
    case SiteType.HOMEALP:
      return gql`
        ${SALE_ARTICLE_SUMMARY_FRAGMENT}
        ${ANNUAL_ARTICLE_SUMMARY_FRAGMENT}
        query GetHomeSummaries($agencyIds: [ID!]) {
          annual: articleRentalpRandomize(accommodationType: ANNUAL, merchantIds: $agencyIds) {
            ... on AnnualAccommodationArticle { ...AnnualArticleSummaryFields }
          }
          sale: articleRentalpRandomize(accommodationType: SALE, merchantIds: $agencyIds) {
            ... on SaleAccommodationArticle { ...SaleArticleSummaryFields }
          }
        }
      `;
    default:
      return gql`
        ${RENTAL_ARTICLE_SUMMARY_FRAGMENT}
        query GetHomeSummaries($agencyIds: [ID!]) {
          summaries: articleRentalpRandomize(accommodationType: RENTAL, merchantIds: $agencyIds) {
            ... on RentalAccommodationArticle { ...RentalArticleSummaryFields }
          }
        }
      `;
  }
}

const REVIEWS_GQL_DATA = gql`
  query GetHomeReviews {
    reviews: reviewsRentalpRandomize { customerName averageRate reviewedAt comment }
  }
`;

const HomePage = () => {

  const intl = useIntl();
  const navigate = useLocaleNavigate();
  const { options: { siteType, asAgency } } = useAppConfig();
  const season = useSeason();


  // data loading

  const { loading: summariesLoading, data: summariesData } = useQuery(SUMMARIES_GQL_DATA(siteType), {
    variables: { ...(!!asAgency) && { agencyIds: [asAgency.id] } },
    ssr: false
  });

  const { loading: reviewsLoading, data: reviewsData } = (siteType === SiteType.RENTALP && !asAgency)
    ? useQuery(REVIEWS_GQL_DATA, { ssr: false })
    : { loading: false, data: { reviews: [] } };

  const { loading: promoLoading, data: promoData } = (siteType === SiteType.RENTALP)
    ? useQuery(PROMOS_GQL_DATA, {
      variables: { ...(!!asAgency) && { agencyIds: [ asAgency.id ] } },
      ssr: false
    })
    : { loading: false, data: { promos: { edges: [] } } };


  // dom interaction

  const onSearch = (c: MainSearchCriteria, siteType: SiteType) => {
    const params = new URLSearchParams();
    const { region, ...criteria } = c;
    !!region && params.set("refId", region.id.toString());
    rebuildQuerySearchParamsFromMain(params, criteria, siteType);
    navigate(`/resorts/${region?.slug ?? "any"}/search${[...params].length > 0 ? `?${params.toString()}` : ""}`);
  }


  // rendering

  const backgroundPath = useMemo<string>(() => {
    switch (siteType) {
      case SiteType.HOMEALP:
        return season === "winter" ? homealpWinterBg : homealpSummerBg;
      case SiteType.SEASONALP:
        return season === "winter" ? seasonalpWinterBg : seasonalpSummerBg;
      default:
        return season === "winter" ? winterBg : summerBg
    }
  }, [ season, siteType ]);

  const promos = promoData?.promos.edges.map((e: any) => e.node) || [];
  const summaries = summariesData?.annual || summariesData?.summaries || [];
  const homealpSaleSummaries = summariesData?.sale || [];
  const reviews = reviewsData?.reviews || [];
  const isMobile = useDisplayMobileOnly();

  return (
    <main className="rl-home">

      <SeoHelmet title={intl.formatMessage(transDefs.title)} />
      <Wrapper.Backgrounded as="section"
                            className="rlh-head"
                            type="boxed"
                            ratio={isMobile ? 125 : 55}
                            imagePath={backgroundPath}>

        <h1 className="catch-phrase">
          {(() => {
            switch (siteType) {
              case SiteType.HOMEALP:
                return intl.formatMessage(transDefs.saleCatch);
              case SiteType.SEASONALP:
                return intl.formatMessage(transDefs.seasonalCatch);
              default:
                return intl.formatMessage(transDefs.catch);
            }
          })()}
        </h1>

        <h3 className="sub-phrase">
          {(() => {
            switch (siteType) {
              case SiteType.HOMEALP:
                return intl.formatMessage(transDefs.searchSaleTitle);
              default:
                return intl.formatMessage(transDefs.searchTitle);
            }
          })()}
        </h3>

        <div className="search-form">
          {siteType === SiteType.HOMEALP
            ? <HomealpSearchForm agencyId={asAgency?.id} onCriteriaChange={onSearch} />
            : <MainSearchForm agencyId={asAgency?.id} onCriteriaChange={onSearch} />
          }
        </div>
      </Wrapper.Backgrounded>

      {(promoLoading || promos.length > 0) &&
        <Wrapper.Slideable as="section"
                           wrapperClassName="rg-carousel-list"
                           withNav={true}
                           title={intl.formatMessage(transDefs.promo)}>
          {promoLoading
            ? <Skeleton containerClassName="rg-skeleton-list" className="rg-card-skeleton" count={10} inline={true} />
            : promos.map((promo: AnyArticleSummary, i: number) => <Summary key={i}
                                                                           data={promo}
                                                                           fixedDiscountView={true} />)
          }
        </Wrapper.Slideable>
      }

      {(siteType === SiteType.HOMEALP && summaries.length > 0) &&
        <Wrapper.Slideable as="section"
                           wrapperClassName="rg-carousel-list"
                           withNav={true}
                           title={intl.formatMessage(transDefs.summaries)}>
          {summariesLoading
            ? <Skeleton containerClassName="rg-skeleton-list" className="rg-card-skeleton" count={10} inline={true} />
            : summaries.map((summary: AnyArticleSummary, i: number) => <Summary key={i} data={summary} />)
          }
        </Wrapper.Slideable>
      }
      {([ SiteType.RENTALP, SiteType.SEASONALP ].includes(siteType)) &&
        <Wrapper.Slideable as="section"
                           wrapperClassName="rg-carousel-list"
                           withNav={true}
                           title={intl.formatMessage(transDefs.summaries)}>
          {summariesLoading
            ? <Skeleton containerClassName="rg-skeleton-list" className="rg-card-skeleton" count={10} inline={true} />
            : summaries.map((summary: AnyArticleSummary, i: number) => <Summary key={i} data={summary} />)
          }
        </Wrapper.Slideable>
      }

      {(siteType === SiteType.HOMEALP && homealpSaleSummaries.length > 0) &&
        <Wrapper.Slideable as="section"
                           wrapperClassName="rg-carousel-list"
                           withNav={true}
                           title={intl.formatMessage(transDefs.summariesSale)}>
          {summariesLoading
            ? <Skeleton containerClassName="rg-skeleton-list" className="rg-card-skeleton" count={10} inline={true}/>
            : homealpSaleSummaries.map((summary: AnyArticleSummary, i: number) => <Summary key={i} data={summary} />)
          }
        </Wrapper.Slideable>
      }

      {(reviewsLoading || reviews.length > 0) &&
        <Wrapper.Slideable as="section"
                           data-nosnippet={true}
                           className="rlh-comments"
                           wrapperClassName="rg-carousel-list"
                           withNav={true}
                           title={intl.formatMessage(transDefs.reviews)}>
          {reviewsLoading
            ? <Skeleton containerClassName="rg-skeleton-list" className="rg-review-skeleton" count={10} inline={true}/>
            : reviews.map((review: any, i: number) => <Comment key={i} data={review}/>)
          }
        </Wrapper.Slideable>
      }

    </main>);

};

export default HomePage;
